import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  TextField,
  Autocomplete,
  Popper,
} from "@mui/material";
import Tabs, { tabsClasses } from "@mui/material/Tabs";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { Card, TableContainer, Button, TableHead } from "@mui/material";
import { styled } from "@mui/material";
import { requestMarketData } from "../../Redux/Reducers/marketDataSlice";
import {
  TABLE_HEAD_VANTAGE as TABLE_HEAD,
  applySortFilter,
  calculateAndFormat,
  getComparator,
  StyledTab,
} from "../../component/Methods";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import VantageListHead from "../../component/ListHead/VantageListHead";
import PerformanceFundPopUp from "./Popup/PerformanceFundPopUp";
import FundStrategyPopUp from "./Popup/FundStrategyPopUp";
import { StrategiesDashboard } from "../../component/Methods/dashboardCalculations";
import PositionPeformance from "./Popup/PositionPerformance";
import BenchMarkTable from "./BenchMarkTable";
import Switch from "@mui/material/Switch";
import Loader from "../../component/Loader";
import { getMarketDataThreshold } from "../../Redux/Reducers/selectors";
import PositionsCalculationToggle from "../../component/PositionsCalculationToggle";
import { useVantageSystemState } from "../../hooks/useOptimizedState";
import {
  isValidNum,
  getSignColor,
  formatCurrency,
  formatPercentPlain,
  formatPercentSigned,
  formatLongCapital,
  formatShortCapital,
  formatNetCapital,
  formatPercentColored,
} from "./utility";

const label = { inputProps: { "aria-label": "Switch demo" } };

const FundFilterPopper = React.forwardRef(function FundFilterPopper(props, ref) {
  const { modifiers = [], ...other } = props;
  return (
    <Popper
      {...other}
      ref={ref}
      placement="bottom-start"
      modifiers={[
        ...modifiers,
        { name: "flip", enabled: false },
        { name: "preventOverflow", enabled: false },
      ]}
    />
  );
});

const Tablecell = styled(TableCell)(({ theme }) => ({
  fontSize: "12px",
  padding: "1px 2px 1px 5px",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(0, 0, 0, 0.04)"
        : "rgba(0, 0, 0, 0.15)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "transparent",
  },
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(255, 255, 255, 0.7)"
        : "rgba(255, 255, 255, 0.08)",
  },
}));

const PerformanceDetails = () => {
  const threshold = useSelector(getMarketDataThreshold);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("aum");
  const [filterName, setFilterName] = useState("");
  const [isGridViewPositions, setIsGridViewPositions] = useState(false);
  const [isGridViewPerformance, setIsGridViewPerformance] = useState(false);
  const [isPositionPerformance, setIsPositionPerformance] = useState(false);
  const [checked, setChecked] = useState(true);
  const [value, setValue] = React.useState(0);
  const [isMarketData, setIsMarketData] = useState(false);
  const [dataFetching, setDataFetching] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);

  const {
    funds,
    trades,
    lots,
    livePrices,
    RawMarketData,
    accountingData,
  } = useVantageSystemState();

  const resultArray = StrategiesDashboard();

  const monitorScreenValuesObj = useMemo(
    () =>
      applySortFilter(
        resultArray,
        getComparator(order, orderBy),
        filterName
      ),
    [resultArray, order, orderBy, filterName]
  );

  const timeoutRef = useRef(null);
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    // Clear the previous timeout if it exists
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setDataFetching(true);
    // Set a new timeout to trigger the function after 20 seconds
    timeoutRef.current = setTimeout(() => {
      setIsMarketData(false);
      dispatch(
        requestMarketData({
          lots: lots,
          threshold: threshold,
          noRequest: true,
          trades: trades,
        })
      );
    }, 20000); // 10 seconds

    // Cleanup the timeout if the component unmounts or RawMarketData changes
    return () => clearTimeout(timeoutRef.current);
  }, [RawMarketData]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  const handleChangeCheck = (event) => {
    setChecked(event.target.checked);
  };

  const findFundAum = (key, array) => {
    if (key && array && array[0]?.funds) {
      const findAum = array[0]?.funds?.find((i) => i.fund === key);
      return findAum?.aum;
    }
  };

  // Fund summary: P(L) from StrategiesDashboard rows; AUM / P(L)% from accounting
  const accFund = useMemo(() => {
    if (!funds) return [];

    const findFunds = funds?.filter(
      (i) =>
        i.abbreviation === "wsf" ||
        i.abbreviation === "pmp" ||
        i.abbreviation === "top" ||
        i.abbreviation === "dbbs" ||
        i.abbreviation === "ral1" ||
        i.abbreviation === "ral2" ||
        i.abbreviation === "jk" ||
        i.abbreviation === "sthw" ||
        i.abbreviation === "sche" ||
        i.abbreviation === "spir"
    );

    return findFunds?.map((fund) => {
      const strategyRows =
        resultArray?.filter((row) => row.fundName === fund.abbreviation) ||
        [];
      const totalPL = strategyRows.reduce(
        (total, row) => total + (parseFloat(row.pL) || 0),
        0
      );
      const entryAUM = parseFloat(findFundAum(fund.fundId, accountingData)) || 0;

      return {
        name: fund.name,
        aum: entryAUM,
        size: totalPL,
        pf: entryAUM > 0 ? (totalPL / entryAUM) * 100 : 0,
      };
    });
  }, [funds, resultArray, accountingData]);

  // Memoize filtered table data
  const filteredTableData = useMemo(() => {
    return monitorScreenValuesObj?.filter((row) => {
      const meetsAumCondition =
        (checked && parseFloat(row?.aum) > 10) || !checked;
      const meetsFundCondition =
        !selectedFund?.abbreviation ||
        row.fundName === selectedFund.abbreviation ||
        row.fundName === selectedFund.label;
      return meetsAumCondition && meetsFundCondition;
    });
  }, [monitorScreenValuesObj, checked, selectedFund]);

  const ALL_FUNDS_OPTION = useMemo(
    () => ({ label: "All funds", name: "All funds", abbreviation: null }),
    []
  );

  const getUniqueFundNames = useMemo(() => {
    const byAbbr = new Map();
    monitorScreenValuesObj?.forEach((row) => {
      if (!row.fundName || byAbbr.has(row.fundName)) return;
      const name = row.fundDisplayName || row.fundName;
      byAbbr.set(row.fundName, {
        label: name,
        name,
        abbreviation: row.fundName,
      });
    });
    return [
      ALL_FUNDS_OPTION,
      ...Array.from(byAbbr.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    ];
  }, [monitorScreenValuesObj, ALL_FUNDS_OPTION]);

  // Memoize static array
  const summary_headers = useMemo(() => ["Fund", "AUM", "P(L)", "P(L)%"], []);

  // Optimize event handlers with useCallback
  const handleCellClick = useCallback(
    (fundName) => {
      navigate(`/portfolio/${fundName}`);
    },
    [navigate]
  );

  const handleCellClickStrategy = useCallback(
    (fundName, strategyName) => {
      navigate(`/portfolio/${fundName}/${strategyName}`);
    },
    [navigate]
  );

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const toggleGridViewPositions = useCallback(() => {
    setIsGridViewPositions((prev) => !prev);
  }, []);

  const toggleGridViewPerformance = useCallback(() => {
    setIsGridViewPerformance((prev) => !prev);
  }, []);

  const handleMarketData = useCallback(() => {
    setIsMarketData(true);
    setDataFetching(false);

    dispatch(
      requestMarketData({ lots: lots, threshold: threshold, trades: trades })
    );

    setTimeout(() => {
      if (!dataFetching) {
        setIsMarketData(false);
      }
    }, 20000);
  }, [dispatch, lots, threshold, trades, dataFetching]);

  const theme = useTheme();

  return isMarketData ? (
    <Loader msg={true} marketLoader={true} />
  ) : (
    <>
      <Box
        sx={{
          // Adjust this value based on header height + padding
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            mb: 0,
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ width: "50%", marginRight: 0, padding: "0px" }}>
            {" "}
            <Box
              sx={{
                border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                borderRadius: "4px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: theme.palette.lightGrey.tableHeader,
                  px: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.lightGrey.textColor }}
                >
                  Longs and Shorts Summary
                </Typography>
                <IconButton
                  onClick={toggleGridViewPositions}
                  aria-label="Toggle GridView"
                  sx={{ height: "20px", width: "20px" }}
                >
                  {/* <FullscreenIcon sx={{ height: "15px", width: "15px" }} /> */}
                </IconButton>
              </Box>
              <Table aria-label="a dense table" size="small">
                <TableHead>
                  <TableRow>
                    {summary_headers?.map((i, index) => {
                      return (
                        <Tablecell
                          align="center"
                          key={index}
                          sx={{
                            borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                            color: theme.palette.lightGrey.textColor,
                            fontSize: theme.typography.bodyText12.fontSize,
                            fontWeight: theme.typography.bodyText12.fontWeight,
                          }}
                        >
                          {i}
                        </Tablecell>
                      );
                    })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {accFund?.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        background: theme.palette.grey[100],
                        "&:hover": {
                          background: theme.palette.grey[200],
                          cursor: "pointer",
                        },
                      }}
                    >
                      <Tablecell
                        align="left"
                        sx={{
                          borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                        }}
                      >
                        {row?.name}
                      </Tablecell>
                      <Tablecell
                        sx={{
                          borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                        }}
                        align="right"
                      >
                        {isValidNum(row?.aum)
                          ? calculateAndFormat(row?.aum)
                          : ""}
                      </Tablecell>
                      <Tablecell
                        style={{
                          borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                          color: getSignColor(row?.size, theme),
                        }}
                        align="right"
                      >
                        {formatCurrency(row?.size)}
                      </Tablecell>
                      <Tablecell
                        style={{
                          borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                          color: getSignColor(row?.pf, theme),
                        }}
                        align="right"
                      >
                        {formatPercentColored(row?.pf)}
                      </Tablecell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Tabs
              value={value}
              onChange={handleChange}
              allowScrollButtonsMobile
              aria-label="scrollable force tabs example"
              sx={{
                [`& .${tabsClasses.scrollButtons}`]: {
                  "&.Mui-disabled": { opacity: 0.3 },
                  borderRadius: "50%",
                  background: "#808080",
                  height: "20px",
                  width: "20px",
                  mt: 2,
                },
              }}
              indicatorColor="white"
              textColor="inherit"
            >
              {/* <StyledTab
                    onClick={() => {
                      setIsPositionPerformance(true);
                    }}
                    label="Positions Performance"
                  /> */}
            </Tabs>
          </Box>
          <Box sx={{ width: "50%", marginRight: 0, padding: "0px" }}>
            <BenchMarkTable marketData={livePrices} compact />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            mt: 0,
          }}
        >
          <Box
            sx={{
              margin: "10px 0 15px",
              pt: 0.5,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              size="small"
              variant="outlined"
              options={getUniqueFundNames}
              value={selectedFund || ALL_FUNDS_OPTION}
              onChange={(event, newValue) => {
                setSelectedFund(newValue?.abbreviation ? newValue : null);
              }}
              getOptionLabel={(option) => option?.name || option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option?.abbreviation === value?.abbreviation
              }
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter(
                  (f) =>
                    f.abbreviation == null ||
                    f.abbreviation?.toLowerCase().includes(q) ||
                    f.name?.toLowerCase().includes(q)
                );
              }}
              disableClearable={!selectedFund}
              slots={{ popper: FundFilterPopper }}
              onClose={() => {
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
              }}
              slotProps={{
                popper: {
                  sx: { zoom: 0.9 },
                  modifiers: [
                    {
                      name: "sameWidth",
                      enabled: true,
                      phase: "beforeWrite",
                      requires: ["computeStyles"],
                      fn: ({ state }) => {
                        state.styles.popper.minWidth = `${state.rects.reference.width}px`;
                        state.styles.popper.width = "auto";
                      },
                    },
                  ],
                },
                listbox: {
                  onMouseDown: (event) => {
                    event.preventDefault();
                  },
                  sx: {
                    py: 0.25,
                    fontSize: 13,
                    "& .MuiAutocomplete-option": {
                      minHeight: "unset",
                      py: "4px",
                      px: 1,
                      alignItems: "flex-start",
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      "&:last-of-type": {
                        borderBottom: "none",
                      },
                    },
                  },
                },
              }}
              sx={{ width: 300 }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                const isAll = option.abbreviation == null;
                return (
                  <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{ display: "block !important", minWidth: 0 }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        lineHeight: 1.25,
                        fontWeight: isAll
                          ? 600
                          : theme.typography.bodyText12.fontWeight,
                        color: theme.palette.lightGrey.textColor,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {option.name || option.abbreviation}
                    </Typography>
                    {!isAll &&
                      option.abbreviation &&
                      option.name !== option.abbreviation && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            lineHeight: 1.25,
                            color: theme.palette.text.secondary,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {option.abbreviation}
                        </Typography>
                      )}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Fund"
                  variant="outlined"
                  size="small"
                />
              )}
            />
            <PositionsCalculationToggle />
          </Box>

          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: "hidden",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TableContainer sx={{ flex: 1, overflow: "auto" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: theme.palette.lightGrey.tableHeader,
                  px: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.lightGrey.textColor }}
                  >
                    Funds and Strategies Performance
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.lightGrey.textColor }}
                  >
                    Hide strategies with zero AUM
                  </Typography>
                  <Switch
                    size="small"
                    checked={checked}
                    onChange={handleChangeCheck}
                    inputProps={{ "aria-label": "controlled" }}
                  />

                  {/* <IconButton
                    onClick={toggleGridViewPerformance}
                    aria-label="Toggle GridView"
                    sx={{ height: "20px", width: "20px" }}
                  >
                    <FullscreenIcon sx={{ height: "15px", width: "15px" }} />
                  </IconButton> */}
                </Box>
              </Box>

              <Table
                size="small"
                stickyHeader
                aria-label="a dense table"
                sx={{
                  height: "max-content",
                  width: "max-content",
                  minWidth: "100%",
                  borderCllapse: "collapse",
                  border: "solid 1px #eee",
                }}
              >
                <VantageListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  noStatus={true}
                  onRequestSort={handleRequestSort}
                />

                <TableBody>
                  {filteredTableData?.map((row, i) => (
                    <StyledTableRow rowheight={0} key={i}>
                      <Tablecell
                        align="left"
                        onClick={() => handleCellClick(row.fundName)}
                        sx={{
                          cursor: "pointer",
                          fontWeight: "500",
                          paddingLeft: "5px",
                          width: 200,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.fundDisplayName || row.fundName}
                      </Tablecell>
                      <Tablecell
                        align="left"
                        onClick={() =>
                          row.strategyName == "CPFactorBW"
                            ? handleCellClick(row.fundName)
                            : handleCellClickStrategy(
                              row.fundName,
                              row.strategyName
                            )
                        }
                        sx={{
                          cursor: "pointer",
                          paddingLeft: "5px",
                          width: "10",
                        }}
                      >
                        {row.strategyName}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatCurrency(row?.aum)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pL, theme),
                        }}
                      >
                        {formatCurrency(row?.pL)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.plLong, theme),
                        }}
                      >
                        {formatCurrency(row?.plLong)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.plShort, theme),
                        }}
                      >
                        {formatCurrency(row?.plShort)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatPercentPlain(row?.sizeLong)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatPercentSigned(row?.sizeShort)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatPercentSigned(row?.netAumPercent)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatLongCapital(row?.longCapital)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatShortCapital(row?.shortCapital)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatNetCapital(row?.netCapital)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.longPerformance, theme),
                        }}
                      >
                        {formatPercentColored(row?.longPerformance)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.shortPerformance, theme, true),
                        }}
                      >
                        {formatPercentColored(row?.shortPerformance)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pLYest, theme),
                        }}
                      >
                        {formatPercentColored(row?.pLYest)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.plPer, theme),
                        }}
                      >
                        {formatPercentColored(row?.plPer)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pLMonth, theme),
                        }}
                      >
                        {row.strategyName == "CPFactorBW" && !row.isSMA
                          ? "-"
                          : formatPercentColored(row?.pLMonth)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pLQuarter, theme),
                        }}
                      >
                        {row.strategyName == "CPFactorBW" && !row.isSMA
                          ? "-"
                          : formatPercentColored(row?.pLQuarter)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pLYear, theme),
                        }}
                      >
                        {row.strategyName == "CPFactorBW" && !row.isSMA
                          ? "-"
                          : formatPercentColored(row?.pLYear)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pLITD, theme),
                        }}
                      >
                        {row.strategyName == "CPFactorBW" && !row.isSMA
                          ? "-"
                          : formatPercentColored(row?.pLITD)}
                      </Tablecell>

                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>

      {/* <PositionPeformance
        title="POSITIONS PERFORMANCE"
        isPositionPerformance={isPositionPerformance}
        setIsPositionPerformance={setIsPositionPerformance}
      /> */}

      {/* <PerformanceFundPopUp
        openPositionsPopup={isGridViewPositions}
        setopenPositionsPopup={setIsGridViewPositions}
        accFund={accFund}
        tablehead={positions}
        title="Longs and Shorts Summary"
      /> */}
      {/* <FundStrategyPopUp
        openPerformancePopup={isGridViewPerformance}
        setopenPerformancePopup={setIsGridViewPerformance}
        checked={checked}
        monitor={filteredTableData}
        title="Funds and Strategies Performance"
      /> */}
    </>
  );
};
export default PerformanceDetails;
